import { useCallback, useEffect, useState } from 'react'
import { useReferenceInfo } from '@/artifact-editor/hooks/useReferenceInfo'
import type { ReferenceInfoByCitationId } from '@/artifact-editor/utils/citation-helpers'
import { fetchArtifactMarkdownFromViewableUrl } from '@/artifacts/fetch/get-artifact-version-viewable-url'
import { wrapTablesWithDiv } from '@/chat-common/util/source-html-editing-functions'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import highlightMarkdownHtmlString from '@/content-viewer/util/markdown-reference-highlighter'

type UseArtifactViewableMarkdownOptions = {
  artifactVersionId: string
  sourceConversationId: string
  highlightSentences?: string[] | null
}

type UseArtifactViewableMarkdownResult = {
  htmlString: string | null
  isLoading: boolean
  hasError: boolean
  retry: () => void
  referenceInfoByCitationId: ReferenceInfoByCitationId
}

export function useArtifactViewableMarkdown(options: UseArtifactViewableMarkdownOptions): UseArtifactViewableMarkdownResult {
  const { artifactVersionId, sourceConversationId, highlightSentences } = options
  const referenceInfoByCitationId = useReferenceInfo(artifactVersionId, sourceConversationId, { includeLegacyFileViewerUrls: false })
  const [markdownString, setMarkdownString] = useState<string | null>(null)
  const [htmlString, setHtmlString] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [refetchCount, setRefetchCount] = useState(0)

  const retry = useCallback(() => {
    setRefetchCount((previous) => previous + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadMarkdown() {
      setIsLoading(true)
      setHasError(false)
      setHtmlString(null)
      setMarkdownString(null)

      try {
        const markdown = await fetchArtifactMarkdownFromViewableUrl(artifactVersionId)
        if (!cancelled) setMarkdownString(markdown)
      } catch {
        if (cancelled) return
        setHasError(true)
        setIsLoading(false)
      }
    }

    loadMarkdown()

    return () => {
      cancelled = true
    }
  }, [artifactVersionId, sourceConversationId, refetchCount])

  useEffect(() => {
    if (markdownString === null) return

    const markdown = markdownString
    let cancelled = false

    async function renderMarkdown() {
      setIsLoading(true)
      setHasError(false)

      try {
        let html = await convertMarkdownToHtml(markdown)

        if (highlightSentences?.length) {
          html = await highlightMarkdownHtmlString(html, highlightSentences)
        }

        if (!cancelled) {
          setHtmlString(wrapTablesWithDiv(html))
        }
      } catch {
        if (cancelled) return
        setHasError(true)
        setHtmlString(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    renderMarkdown()

    return () => {
      cancelled = true
    }
  }, [artifactVersionId, sourceConversationId, markdownString, highlightSentences])

  return { htmlString, isLoading, hasError, retry, referenceInfoByCitationId }
}
